import type { SiteEvidenceDocument, SedSectionId, EvidenceStatus } from './siteEvidence';

export type SedRebuildVerdict = 'safe' | 'caution' | 'block-rollback';

export type SedRebuildOutcome =
  | 'success'
  | 'caution'
  | 'blocked'
  | 'rolled-back'
  | 'overridden'
  | 'dry-run';

export type SedRebuildTriggerSource =
  | 'manual-rebuild'
  | 'retry-bootstrap'
  | 'first-bootstrap'
  | 'top-up'
  | 'section-refresh'
  | 'admin'
  | 'unknown';

export interface FieldRef {
  sectionId: SedSectionId;
  fieldPath: string;
}

export interface SectionSnapshot {
  sectionId: SedSectionId;
  status: EvidenceStatus | 'missing';
  verifiedFieldCount: number;
  citationLinks: number;
}

export interface SedSnapshot {
  siteId: string;
  sedId: string | null;
  snapshotTakenAt: string;
  /** 0–100 percentage (matches UI completeness display). */
  completenessPct: number;
  verifiedSectionCount: number;
  verifiedFieldCount: number;
  citationCount: number;
  sectionSummaries: Partial<Record<SedSectionId, SectionSnapshot>>;
  rawSedDoc: SiteEvidenceDocument | null;
  gcsPath?: string;
}

export interface SedDiff {
  completenessDelta: number;
  verifiedSectionsLost: SedSectionId[];
  verifiedSectionsGained: SedSectionId[];
  verifiedFieldsLost: FieldRef[];
  citationsLost: number;
  regressedSections: SedSectionId[];
  verdict: SedRebuildVerdict;
}

export interface RebuildResult {
  rebuildId: string;
  siteId: string;
  outcome: SedRebuildOutcome;
  verdict: SedRebuildVerdict;
  diff: SedDiff | null;
  before: SedSnapshot | null;
  after: SedSnapshot | null;
  sedId?: string;
  completeness?: number;
  warning?: string;
  blocked?: boolean;
}

export interface SedRebuildLogEntry {
  rebuildId: string;
  siteId: string;
  /**
   * #sed-owner-path — the ACTOR: who ran this rebuild. NEVER use it to build a
   * Firestore path; use `ownerUid` for that.
   *
   * It used to serve as both, which is why the write landed at
   * `users/{caller}/sites/{siteId}/sedRebuildLog/...` on a company-shared site
   * — a path whose parent site doc does not exist. The rule there gates on
   * `parentSiteWritable(userId, siteId)` (and, since #169, on
   * `parentSiteInCompanyTree` too), both of which `get()` that parent doc;
   * `get()` on a missing doc is null, `.data` on null ERRORS, and a rules
   * evaluation error is a DENY. So the write did not land in the wrong place,
   * it failed outright and threw, taking the whole rebuild with it.
   */
  uid: string;
  /**
   * #sed-owner-path — the PATH anchor: the uid whose
   * `users/{uid}/sites/{siteId}` subtree actually holds this site, from
   * `siteOwnerUid(site, callerUid)`. Equals `uid` for own sites.
   *
   * Deliberately separate from `uid` rather than replacing it: relabelling the
   * actor as the owner would make an audit row claim the owner ran a member's
   * rebuild, which is worse than a misfiled row. Same split as
   * `SiteEntity._ownerUid`, which is likewise a path annotation and not an
   * identity. Optional only for back-compat with rows written before this
   * field existed; every writer sets it.
   */
  ownerUid?: string;
  trigger: SedRebuildTriggerSource;
  startedAt: string;
  completedAt: string;
  verdict: SedRebuildVerdict;
  outcome: SedRebuildOutcome;
  completenessDelta: number;
  verifiedSectionsLost: SedSectionId[];
  regressedSections: SedSectionId[];
  preSnapshotGcsPath?: string;
  postSnapshotGcsPath?: string;
  preSnapshotSummary?: Omit<SedSnapshot, 'rawSedDoc'>;
  postSnapshotSummary?: Omit<SedSnapshot, 'rawSedDoc'>;
  overrideRationale?: string;
  dryRun?: boolean;
}

export class SedRebuildBlockedError extends Error {
  readonly name = 'SedRebuildBlockedError';
  readonly rebuildId: string;
  readonly siteId: string;
  readonly diff: SedDiff;
  readonly before: SedSnapshot;
  readonly after: SedSnapshot;

  constructor(args: {
    message: string;
    rebuildId: string;
    siteId: string;
    diff: SedDiff;
    before: SedSnapshot;
    after: SedSnapshot;
  }) {
    super(args.message);
    this.rebuildId = args.rebuildId;
    this.siteId = args.siteId;
    this.diff = args.diff;
    this.before = args.before;
    this.after = args.after;
  }
}
